import React, { useState } from "react";
import { Flex, Text, Button, TextInput } from "@jsq/ds/core";
import { useFormContext } from "@jsq/ds/forms";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { CustomFieldOption } from "../../types";
import * as styles from "../styles_vanilla.css";
import { PrefillOptionsPanel } from "./PrefillOptionsPanel";

interface Props {
  name: string;
  label: string;
  helpText?: string;
}

interface FormValues {
  [key: string]: unknown;
}

interface FieldOptionsFormValues extends FormValues {
  [key: string]:
    Omit<CustomFieldOption, "id">[] | string | number | boolean | Date | null | undefined;
}

const FieldOptionsEditor: React.FC<Props> = ({ name, label, helpText }) => {
  const { values, setFieldValue, errors, touched } = useFormContext<FieldOptionsFormValues>();
  const options: Omit<CustomFieldOption, "id">[] = Array.isArray(values[name])
    ? values[name]
    : [];

  // State for prefill functionality
  const [showPrefillOptions, setShowPrefillOptions] = useState(false);

  // Check for validation errors on the options field
  const fieldError = errors[name];
  const isFieldTouched = touched[name];
  const showFieldError = isFieldTouched && fieldError;

  // Helper function to check if a specific option has an empty label
  const isOptionEmpty = (option: Omit<CustomFieldOption, "id">) => {
    return !option?.label || typeof option.label !== "string" || option.label.trim() === "";
  };

  const addOption = () => {
    const newOption = {
      label: "",
      value: "", // Keep for backward compatibility, but won't be used
      isActive: true,
    };
    void setFieldValue(name, [...options, newOption]);
  };

  const removeOption = (index: number) => {
    const updatedOptions = options.filter((_, i) => i !== index);
    void setFieldValue(name, updatedOptions);
  };

  const updateOptionLabel = (index: number, label: string) => {
    const updatedOptions = [...options];
    updatedOptions[index] = {
      ...updatedOptions[index],
      label,
      // Keep value for backward compatibility, but it won't be sent to backend
      value:
        label
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, "")
          .replace(/\s+/g, "_")
          .trim() || label,
    };
    void setFieldValue(name, updatedOptions);
  };

  const togglePrefillOptions = () => {
    setShowPrefillOptions(!showPrefillOptions);
  };

  const handleClosePrefill = () => {
    setShowPrefillOptions(false);
  };

  return (
    <div>
      <Text variant="regular-md" className={styles.optionsLabel}>
        {label}
      </Text>
      {helpText && (
        <Text variant="regular-sm" color="neutral-600" className={styles.optionsHelpText}>
          {helpText}
        </Text>
      )}

      <div className={styles.optionsContainer}>
        {options.map((option, index) => {
          const hasEmptyLabel = isOptionEmpty(option);
          const shouldHighlightError = showFieldError && hasEmptyLabel;

          return (
            <Flex key={index} alignItems="center" gap="12px" className={styles.optionRow}>
              <div className={styles.optionInput}>
                <TextInput
                  placeholder="Enter option label (e.g., High Risk, Medium Risk)"
                  value={option.label}
                  onChange={(e) => updateOptionLabel(index, e.target.value)}
                  data-testid={`option-label-${index}`}
                  isFullWidth
                  errorMessage={shouldHighlightError ? "Option label is required" : undefined}
                />
              </div>

              <Button
                variant="neutral"
                icon="trash"
                onClick={() => removeOption(index)}
                disabled={options.length <= 1}
                data-testid={`remove-option-${index}`}
              />
            </Flex>
          );
        })}

        {options.length === 0 && (
          <Flex
            alignItems="center"
            justifyContent="center"
            className={styles.emptyOptionsContainer}
          >
            <div>
              <Icon type="file" size="large" className={styles.emptyOptionsIcon} />
              <Text variant="regular-md" color="neutral-600">
                No options added yet
              </Text>
            </div>
          </Flex>
        )}

        <Button
          variant="neutral"
          icon="plus"
          iconPosition="left"
          onClick={addOption}
          data-testid="add-option-button"
        >
          Add option
        </Button>

        <Button
          variant="neutral"
          icon="download"
          iconPosition="left"
          onClick={togglePrefillOptions}
          data-testid="prefill-with-button"
        >
          Prefill options
        </Button>

        <PrefillOptionsPanel
          show={showPrefillOptions}
          onClose={handleClosePrefill}
          fieldName={name}
          currentOptions={options}
        />
      </div>

      {/* Display field-level error message */}
      {showFieldError && (
        <Text
          variant="regular-sm"
          className={styles.fieldErrorMessage}
          style={{ color: Colors.Danger600 }}
        >
          {fieldError}
        </Text>
      )}

      {options.length > 0 && (
        <Text variant="regular-xl" color="neutral-500" className={styles.optionsTip}>
          Tip: Enter descriptive labels for your options. Unique identifiers will be
          automatically generated for data storage.
        </Text>
      )}
    </div>
  );
};

export default FieldOptionsEditor;
